import { useState } from 'react'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import type { PersonalDocumentEmployeeResponse, ServiceModuleModel } from '@/api/model'
import { GlobalUploadCard } from './global-components/GlobalUploadCard'
import { EmployeeDocumentsCard } from './employee-components/EmployeeDocumentsCard'
import { UploadDocumentsDialog } from './components/UploadDocumentsDialog'
import { EmployeeDocumentsDialog } from './employee-components/EmployeeDocumentsDialog'
import { LatestDocumentsDialog } from './employee-components/LatestDocumentsDialog'

interface Props {
  module: ServiceModuleModel
}

/** Target of an upload: files + optionally the employee (global = without employee). */
interface UploadTarget {
  files: File[]
  employee: PersonalDocumentEmployeeResponse | null
}

/**
 * Personal documents page (card click on a PersonalDocuments module). Two
 * sections stacked: the global upload card (assignment by personnel number) and
 * the employee list with direct upload per employee. Composition root: holds
 * only the dialog state, the cards/dialogs fetch their data themselves.
 */
export function PersonalDocumentsPage({ module }: Props) {
  const serviceModuleId = module.id

  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null)
  const [viewEmployee, setViewEmployee] = useState<PersonalDocumentEmployeeResponse | null>(null)
  const [isLatestOpen, setIsLatestOpen] = useState(false)

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={module.name ?? ''} />

      <GlobalUploadCard
        onFiles={(files) => setUploadTarget({ files: Array.from(files), employee: null })}
      />

      <EmployeeDocumentsCard
        serviceModuleId={serviceModuleId}
        onView={setViewEmployee}
        onUpload={(files, employee) => setUploadTarget({ files: Array.from(files), employee })}
        onOpenLatest={() => setIsLatestOpen(true)}
      />

      {uploadTarget && (
        <UploadDocumentsDialog
          serviceModuleId={serviceModuleId}
          employee={uploadTarget.employee}
          initialFiles={uploadTarget.files}
          onClose={() => setUploadTarget(null)}
        />
      )}

      {viewEmployee && (
        <EmployeeDocumentsDialog
          serviceModuleId={serviceModuleId}
          employee={viewEmployee}
          onClose={() => setViewEmployee(null)}
        />
      )}

      {isLatestOpen && (
        <LatestDocumentsDialog
          serviceModuleId={serviceModuleId}
          onClose={() => setIsLatestOpen(false)}
        />
      )}
    </PageLayout>
  )
}
